import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DatabaseZap, ArrowLeft, RefreshCw, AlertTriangle, Check, FileSpreadsheet, Columns, ListChecks,
  FlaskConical, ShieldCheck, FileText, Send, MessageSquare, ExternalLink,
} from 'lucide-react';
import { InventoryPage } from '../inventory/InventoryChrome';
import { RubyLoader } from '../../components/RubyLoader';
import { RubyGemMark } from '../../components/RubyGemMark';
import { toastService } from '../../services/toastService';
import { migrationApi, type MigrationJob, type MigrationStatus } from '../../services/migrationApi';
import {
  RUBY, StatusPill, Btn, Banner, CheckRow, BUSY_STATUSES, dryRunRunning, fmtDateTime, sourceLabel, toastApiError,
} from './migration/shared';
import { FilesStep } from './migration/FilesStep';
import { MappingStep } from './migration/MappingStep';
import { ReviewStep } from './migration/ReviewStep';
import { DryRunStep, ApplyStep, ReportStep } from './migration/RunSteps';

/**
 * Super Admin → Tools → Data Migration → one job.
 * Stepper (Files → Mapping → Review → Dry run → Approve & apply → Report) on the
 * left, the step on the right of it, activity + client messages on the far right.
 * Polls every 4s while the worker is busy.
 */

type StepId = 'files' | 'mapping' | 'review' | 'dryrun' | 'apply' | 'report';
const STEPS: { id: StepId; label: string; icon: React.FC<any> }[] = [
  { id: 'files', label: 'Files', icon: FileSpreadsheet },
  { id: 'mapping', label: 'Mapping', icon: Columns },
  { id: 'review', label: 'Review', icon: ListChecks },
  { id: 'dryrun', label: 'Dry run', icon: FlaskConical },
  { id: 'apply', label: 'Approve & apply', icon: ShieldCheck },
  { id: 'report', label: 'Report', icon: FileText },
];

const ORDER: MigrationStatus[] = ['uploading', 'submitted', 'analysing', 'mapped', 'dry_run_ready', 'approved', 'backing_up', 'applying', 'stocking', 'applied', 'rolling_back', 'rolled_back'];
const reached = (job: MigrationJob, s: MigrationStatus) => ORDER.indexOf(job.status) >= ORDER.indexOf(s);

function stepForStatus(job: MigrationJob): StepId {
  switch (job.status) {
    case 'uploading':
    case 'submitted': return (job.files || []).length ? 'mapping' : 'files';
    case 'analysing': return 'mapping';
    case 'mapped': return job.dryRunAt ? 'dryrun' : 'review';
    case 'dry_run_ready': return 'apply';
    case 'approved':
    case 'backing_up':
    case 'applying':
    case 'stocking': return 'apply';
    case 'failed': return job.progress?.total ? 'report' : 'mapping';
    default: return 'report';
  }
}

function stepDone(job: MigrationJob, id: StepId): boolean {
  switch (id) {
    case 'files': return (job.files || []).length > 0;
    case 'mapping': return !!job.preview && reached(job, 'mapped');
    case 'review': return reached(job, 'dry_run_ready');
    case 'dryrun': return reached(job, 'dry_run_ready');
    case 'apply': return reached(job, 'applied');
    case 'report': return job.status === 'applied' || job.status === 'rolled_back';
  }
}

const ActivityPanel: React.FC<{ job: MigrationJob; onJob: (j: MigrationJob) => void }> = ({ job, onJob }) => {
  const [msg, setMsg] = useState('');
  const [needsInput, setNeedsInput] = useState(false);
  const [busy, setBusy] = useState(false);
  const events = [...(job.events || [])].reverse();

  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      const r = await migrationApi.message(job.tenantId, job.jobId, msg.trim(), needsInput);
      onJob(r.job);
      setMsg('');
      setNeedsInput(false);
      toastService.success(needsInput ? 'Question sent — the client is asked for input' : 'Message sent to the client');
    } catch (e) {
      toastApiError(e, 'Could not send the message');
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside className="bg-white rounded-xl border border-stone-200 flex flex-col min-h-0 xl:sticky xl:top-4 xl:max-h-[calc(100vh-140px)]">
      <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-2">
        <MessageSquare size={16} className="text-stone-500" />
        <h2 className="flex-1 font-semibold text-stone-900">Activity</h2>
        {job.needsInput && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900">WAITING ON CLIENT</span>}
      </div>
      <div className="p-4 border-b border-stone-100 space-y-2">
        <textarea
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          rows={3}
          placeholder="Message the client…"
          aria-label="Message to the client"
          className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm text-stone-800 outline-none focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/15"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[160px]"><CheckRow checked={needsInput} onChange={setNeedsInput}>Ask the client (needs input)</CheckRow></div>
          <Btn kind="black" icon={<Send size={14} />} busy={busy} disabled={!msg.trim()} onClick={send}>Send</Btn>
        </div>
      </div>
      <ol className="flex-1 overflow-y-auto divide-y divide-stone-100">
        {events.length === 0 && <li className="p-4 text-sm text-stone-400">No activity yet.</li>}
        {events.map((e, i) => (
          <li key={`${e.at}-${i}`} className="px-4 py-3">
            <div className="flex items-center gap-2 text-[11px] text-stone-400">
              <span className="font-semibold uppercase tracking-wider text-stone-500">{e.kind.replace(/_/g, ' ')}</span>
              <span>· {fmtDateTime(e.at)}</span>
              {e.visibleToClient && <span className="ml-auto px-1.5 py-0.5 rounded bg-stone-100 text-stone-500">client sees</span>}
            </div>
            <div className={`text-sm mt-0.5 ${e.kind === 'failed' || e.kind === 'attention' ? 'text-rose-700' : e.kind === 'client_reply' ? 'text-stone-900 font-medium' : 'text-stone-700'}`}>{e.message}</div>
            {e.by && e.by !== 'system' && <div className="text-[11px] text-stone-400 mt-0.5 truncate">{e.by}</div>}
          </li>
        ))}
      </ol>
    </aside>
  );
};

const DataMigrationJobPage: React.FC = () => {
  const { tenantId = '', jobId = '' } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState<MigrationJob | null>(null);
  const [locations, setLocations] = useState<{ locationId: string; name: string }[]>([]);
  const [productTypes, setProductTypes] = useState<{ productTypeId: string; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<StepId | null>(null);
  const autoStep = useRef<string>('');

  const load = useCallback(async () => {
    try {
      const r = await migrationApi.getJob(tenantId, jobId);
      setJob(r.job);
      setLocations(r.locations || []);
      setProductTypes(r.productTypes || []);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load the migration');
    } finally {
      setLoading(false);
    }
  }, [tenantId, jobId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  // Open on the step the status implies; afterwards only follow the big moves
  // (analysis started, import finished / rolled back) so an operator's own
  // edit never yanks them to another step.
  useEffect(() => {
    if (!job) return;
    const prev = autoStep.current;
    if (prev === job.status) return;
    autoStep.current = job.status;
    if (!prev) setStep(stepForStatus(job));
    else if (job.status === 'analysing') setStep('mapping');
    else if (job.status === 'backing_up') setStep('apply');
    else if (['applied', 'rolling_back', 'rolled_back'].includes(job.status)) setStep('report');
  }, [job]);

  // Poll while the worker is busy.
  const busy = !!job && (BUSY_STATUSES.includes(job.status) || dryRunRunning(job));
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => { load(); }, 4000);
    return () => clearInterval(t);
  }, [busy, load]);

  // A mutation response carries the job but not locations — merge, keep the rest.
  const onJob = useCallback((j: MigrationJob) => setJob((prev) => ({ ...(prev || {}), ...j })), []);

  const actions = (
    <div className="flex items-center gap-2">
      {busy && <span className="text-neutral-300 hidden sm:flex items-center gap-2 text-sm"><RubyGemMark size={16} drawing color="currentColor" />Working…</span>}
      <button
        type="button"
        onClick={() => { setLoading(true); load(); }}
        disabled={loading}
        className="h-11 w-11 grid place-items-center rounded-lg border border-neutral-700 text-neutral-300 hover:text-white disabled:opacity-60"
        aria-label="Refresh"
        title="Refresh"
      >
        {loading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw size={16} />}
      </button>
      <button
        type="button"
        onClick={() => navigate(`/tenant/${encodeURIComponent(tenantId)}`)}
        className="h-11 px-3 rounded-lg border border-neutral-700 text-neutral-300 hover:text-white text-sm hidden sm:flex items-center gap-1.5"
      >
        <ExternalLink size={14} />Tenant
      </button>
    </div>
  );

  const title = job?.tenantName ? `Migration · ${job.tenantName}` : 'Migration';

  return (
    <InventoryPage icon={DatabaseZap} title={title} crumb="Tools › Data Migration" actions={actions} wide>
      <button type="button" onClick={() => navigate('/tools/data-migration')} className="h-11 -mt-2 mb-2 inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900">
        <ArrowLeft size={16} />All migrations
      </button>

      {!job ? (
        error ? (
          <Banner tone="rose" icon={<AlertTriangle size={16} />}>
            <div className="flex items-center gap-3"><span className="flex-1">{error}</span><Btn onClick={() => { setLoading(true); load(); }}>Retry</Btn></div>
          </Banner>
        ) : <RubyLoader variant="page" label="Loading migration" />
      ) : (
        <>
          <div className="bg-white rounded-xl border border-stone-200 px-4 py-3 mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <StatusPill status={job.status} needsInput={job.needsInput} />
            <span className="text-stone-500">Source <b className="text-stone-800">{sourceLabel(job.source || job.sourceGuess)}</b></span>
            <span className="text-stone-500">Files <b className="text-stone-800">{(job.files || []).length}</b></span>
            {job.stats?.toCreate !== undefined && <span className="text-stone-500">To create <b className="text-stone-800">{job.stats.toCreate}</b></span>}
            <span className="text-stone-500">Updated <b className="text-stone-800">{fmtDateTime(job.updatedAt)}</b></span>
            <span className="text-stone-400 text-xs font-mono ml-auto">{job.jobId}</span>
          </div>
          {error && <div className="mb-4"><Banner tone="rose" icon={<AlertTriangle size={16} />}>{error}</Banner></div>}
          {job.status === 'failed' && job.error && <div className="mb-4"><Banner tone="rose" icon={<AlertTriangle size={16} />}><b>Failed:</b> {job.error}</Banner></div>}
          {job.status === 'analysing' && (
            <div className="mb-4">
              <Banner tone="sky" icon={<span style={{ color: RUBY }}><RubyGemMark size={16} drawing color="currentColor" /></span>}>Analysing the files — this page updates by itself.</Banner>
            </div>
          )}

          <div className="grid gap-4 lg:grid-cols-[200px_minmax(0,1fr)] xl:grid-cols-[200px_minmax(0,1fr)_340px] items-start">
            <nav aria-label="Migration steps" className="bg-white rounded-xl border border-stone-200 p-2 lg:sticky lg:top-4">
              <ol className="flex lg:flex-col gap-1 overflow-x-auto">
                {STEPS.map((s, i) => {
                  const on = step === s.id;
                  const done = stepDone(job, s.id);
                  const Icon = s.icon;
                  return (
                    <li key={s.id} className="shrink-0">
                      <button
                        type="button"
                        onClick={() => setStep(s.id)}
                        aria-current={on ? 'step' : undefined}
                        className={`w-full min-h-[44px] px-3 rounded-lg flex items-center gap-2.5 text-sm text-left whitespace-nowrap transition ${on ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-50'}`}
                      >
                        <span
                          className="w-6 h-6 rounded-full grid place-items-center text-[11px] font-semibold shrink-0"
                          style={done ? { background: RUBY, color: '#fff' } : on ? { background: '#fff', color: '#1c1917' } : { background: '#f5f5f4', color: '#78716c' }}
                        >
                          {done ? <Check size={13} /> : i + 1}
                        </span>
                        <Icon size={15} className={on ? 'text-stone-300' : 'text-stone-400'} />
                        <span className="font-medium">{s.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </nav>

            <div className="min-w-0">
              {step === 'files' && <FilesStep job={job} reload={load} onJob={onJob} />}
              {step === 'mapping' && <MappingStep job={job} locations={locations} productTypes={productTypes} onJob={onJob} reload={load} />}
              {step === 'review' && <ReviewStep job={job} locations={locations} reload={load} />}
              {step === 'dryrun' && <DryRunStep job={job} onJob={onJob} reload={load} />}
              {step === 'apply' && <ApplyStep job={job} onJob={onJob} reload={load} />}
              {step === 'report' && <ReportStep job={job} onJob={onJob} reload={load} />}
            </div>

            <div className="lg:col-span-2 xl:col-span-1 min-w-0">
              <ActivityPanel job={job} onJob={onJob} />
            </div>
          </div>
        </>
      )}
    </InventoryPage>
  );
};

export default DataMigrationJobPage;
